<script setup lang="ts">
/**
 * Breadcrumb. DESIGN.md section 9 lists it as part of the app shell `FND-11`
 * owes.
 *
 * Built from the catalog's explicit `parent`, not from URL segments. `/clients/new`
 * is a child of the client list but `/referrals/inbox` is not a child of
 * `/referrals/mine`, and a path-splitting breadcrumb gets the second one wrong.
 *
 * It renders only where it earns its place — a screen with no parent shows
 * nothing rather than a one-item trail.
 */

import { computed } from 'vue'

import MpIcon from '@/components/base/MpIcon.vue'
import { SCREENS_BY_ID, type ScreenDefinition } from '@/screens/catalog'

const props = defineProps<{ screen: ScreenDefinition }>()

const trail = computed<ScreenDefinition[]>(() => {
  const chain: ScreenDefinition[] = []
  let cursor: ScreenDefinition | undefined = props.screen.parent ? SCREENS_BY_ID[props.screen.parent] : undefined
  // Bounded: the catalog is finite and a cycle would be a data error, so the
  // guard is a depth cap rather than a visited set.
  let depth = 0
  while (cursor && depth < 5) {
    chain.unshift(cursor)
    cursor = cursor.parent ? SCREENS_BY_ID[cursor.parent] : undefined
    depth += 1
  }
  return chain
})
</script>

<template>
  <nav
    v-if="trail.length"
    class="mp-breadcrumb"
    aria-label="Breadcrumb"
  >
    <ol class="mp-breadcrumb__list">
      <li
        v-for="crumb in trail"
        :key="crumb.id"
        class="mp-breadcrumb__item"
      >
        <RouterLink :to="crumb.path">
          {{ crumb.title }}
        </RouterLink>
        <MpIcon
          name="chevron-right"
          size="sm"
        />
      </li>
      <li
        class="mp-breadcrumb__item"
        aria-current="page"
      >
        {{ props.screen.title }}
      </li>
    </ol>
  </nav>
</template>

<style scoped>
.mp-breadcrumb__list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--font-size-small);
  color: var(--text-secondary);
}

.mp-breadcrumb__item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.mp-breadcrumb__item[aria-current='page'] {
  color: var(--text-primary);
  font-weight: 600;
}
</style>
